import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { SidebarWithSections } from '@teetsh/app/src/components/SubSidebar/SidebarWithSections';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ArrowDownCircleIcon,
  PrinterIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { FontsCoupleSelector } from '@teetsh/app/src/components/Form/FontsCoupleSelector';
import { FontSizeSelector } from '@teetsh/app/src/components/FontSizeSelector';
import { MarginsFieldWrapper } from '@teetsh/app/src/components/Margins';
import { ColorSelector } from '@teetsh/app/src/components/Form/ColorSelector';
import useDownloadEleve from 'queries/eleves/useDownloadEleves';
import ButtonGroup from 'components/Form/ButtonGroup';
import { ElevesPDFSettings } from 'types/eleves';
import { UserLocale } from 'types/user';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  limitedBgSet: boolean;
  viewId: string;
  viewName: string;
  lng: UserLocale;
}

export function Sidebar(props: Props) {
  const { limitedBgSet, viewId, viewName, lng } = props;
  const { values: settings } = useFormState<ElevesPDFSettings>();
  const { download, isDownloading } = useDownloadEleve();
  const onDownload = () => download({ viewId, viewName, settings, lng });
  const { t } = useTranslation(['common', 'sequence']);

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="fontSize"
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                limitedBgSet={limitedBgSet}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.format')}>
          <Field
            name="landscape"
            render={({ input }) => (
              <ButtonGroup>
                <Button
                  active={!input.value}
                  onClick={() => input.onChange(false)}
                >
                  {t('common:pdf.portrait')}
                </Button>
                <Button
                  active={input.value}
                  onClick={() => input.onChange(true)}
                >
                  {t('common:pdf.landscape')}
                </Button>
              </ButtonGroup>
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
